import { useRef, useState, forwardRef, useImperativeHandle } from "react";
import { colors } from "../_constants";
import { Trash2 } from "lucide-react";

export type DrawingBoardHandle = {
  getImageBlob: () => Promise<Blob | null>;
  clearCanvas: () => void;
};

type DrawEvent =
  | React.MouseEvent<HTMLCanvasElement>
  | React.TouchEvent<HTMLCanvasElement>;

type CanvasPoint = {
  x: number;
  y: number;
};

const DrawingBoard = forwardRef<DrawingBoardHandle>(function DrawingBoard(
  _,
  ref
) {
  const [drawingColor, setDrawingColor] = useState("#ff6b6b");
  const [isDrawing, setIsDrawing] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Helper function to get accurate mouse/touch position
  const getMousePos = (canvas: HTMLCanvasElement, e: DrawEvent): CanvasPoint => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    let clientX: number;
    let clientY: number;

    if ("touches" in e) {
      const firstTouch = e.touches[0] ?? e.changedTouches[0];
      if (!firstTouch) {
        return { x: 0, y: 0 };
      }
      clientX = firstTouch.clientX;
      clientY = firstTouch.clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  // Drawing functionality for mouse
  const startDrawing = (e: DrawEvent) => {
    if (!canvasRef.current) return;

    e.preventDefault();
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const pos = getMousePos(canvas, e);
    const ctx = canvas.getContext("2d")!;
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e: DrawEvent) => {
    if (!isDrawing || !canvasRef.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const pos = getMousePos(canvas, e);
    const ctx = canvas.getContext("2d")!;
    ctx.lineWidth = 4;
    ctx.lineCap = "round";
    ctx.strokeStyle = drawingColor;
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const stopDrawing = (e?: DrawEvent) => {
    if (e) e.preventDefault();
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d")!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  function downloadBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
  // Expose the canvas ref to parent via forwardRef
  useImperativeHandle(ref, () => ({
    getImageBlob: async () => {
      if (!canvasRef.current) return null;

      const canvas = canvasRef.current;
      const tempCanvas = document.createElement("canvas");
      const tempCtx = tempCanvas.getContext("2d");
      if (!tempCtx) return null;

      // Set same dimensions as original canvas
      tempCanvas.width = canvas.width;
      tempCanvas.height = canvas.height;

      // Fill with white background
      tempCtx.fillStyle = "white";
      tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

      // Draw the original canvas content on top
      tempCtx.drawImage(canvas, 0, 0);
      const imageBlob = await new Promise<Blob | null>((resolve) => {
        tempCanvas.toBlob(
          (blob) => {
            resolve(blob);
          },
          "image/png",
          1
        );
      });

      if (imageBlob) {
        downloadBlob(imageBlob, "drawing.png");
      }

      if (imageBlob && imageBlob.size < 100) return null;

      return imageBlob;
    },
    clearCanvas: clearCanvas,
  }));

  return (
    <div>
      {/* Drawing Canvas */}
      <canvas
        ref={canvasRef}
        width={500}
        height={300}
        className="w-full mx-auto border-2 border-purple-200 rounded-2xl bg-white cursor-crosshair block touch-none mb-3"
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
        onTouchCancel={stopDrawing}
        style={{ touchAction: "none" }}
      />

      {/* Color Picker */}
      <div className="flex justify-center gap-3 mb-2">
        {colors.map((colorOption) => (
          <button
            key={colorOption.color}
            onClick={() => setDrawingColor(colorOption.color)}
            className={`w-10 h-10 rounded-full transition-all duration-200 transform hover:scale-110 ${
              drawingColor === colorOption.color
                ? "ring-4 ring-purple-400 scale-110"
                : "hover:ring-2 hover:ring-purple-200"
            }`}
            style={{ backgroundColor: colorOption.color }}
            title={colorOption.name}
          />
        ))}
      </div>

      {/* Active Color Indicator and Clear Button */}
      <div className="flex items-center justify-center gap-4 mb-1">
        <div className="flex items-center gap-2">
          <span className="text-purple-700 font-bold text-sm uppercase">
            Active Color
          </span>
          <div
            className="w-6 h-6 rounded-full"
            style={{ backgroundColor: drawingColor }}
          ></div>
        </div>
        <button
          onClick={clearCanvas}
          className="flex items-center gap-2 text-red-500 font-semibold text-sm hover:text-red-600 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
          Clear
        </button>
      </div>

      {/* Instruction Text */}
      <p className="text-center text-purple-500 text-sm">
        Draw only the outlines {"\u2022"} Don&apos;t fill inside
      </p>
    </div>
  );
});

export default DrawingBoard;
